import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileText, Upload, ExternalLink, Download, Link2, RefreshCw, Send, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateOnly, formatDateTime } from "@/lib/date";
import DocStatusBadge from "./DocStatusBadge";

const FORMS_PER_PAGE = 8;
const HANDOUTS_PER_PAGE = 5;

// Member + parent (read-only) Document Depot: assigned Forms (with submit flow) and Handouts.
// Members submit (submission date/time is stamped automatically) and — only when the org
// upload toggle is on and the form has a destination folder — can optionally attach a file.
export default function DepotLists({ forms = [], handouts = [], readOnly = false, onSubmit, submittingId, uploadsEnabled = false }) {
  return (
    <Tabs defaultValue="forms" data-testid="member-document-depot">
      <TabsList className="mb-4" data-testid="member-depot-tabs">
        <TabsTrigger value="forms" data-testid="member-tab-forms">My Forms</TabsTrigger>
        <TabsTrigger value="handouts" data-testid="member-tab-handouts">Handouts</TabsTrigger>
      </TabsList>

      <TabsContent value="forms">
        <FormsTab forms={forms} readOnly={readOnly} onSubmit={onSubmit} submittingId={submittingId} uploadsEnabled={uploadsEnabled} />
      </TabsContent>

      <TabsContent value="handouts">
        <HandoutsTab handouts={handouts} />
      </TabsContent>
    </Tabs>
  );
}

function Pager({ page, pages, onChange, testId }) {
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-center gap-3" data-testid={testId}>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid={`${testId}-prev`}>
        <ChevronLeft className="mr-1 h-4 w-4" /> Prev
      </Button>
      <span className="text-sm text-slate-500" data-testid={`${testId}-info`}>Page {page} of {pages}</span>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid={`${testId}-next`}>
        Next <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

function FormsTab({ forms, readOnly, onSubmit, submittingId, uploadsEnabled }) {
  const [nameFilter, setNameFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Assigned form names, A→Z.
  const names = useMemo(
    () => [...new Set(forms.map((f) => f.name))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [forms]
  );

  // Pin unsubmitted forms (never submitted) ahead of submitted ones; then by creation order.
  const sorted = useMemo(() => {
    return [...forms].sort((a, b) => {
      const aSub = a.submitted_at ? 1 : 0;
      const bSub = b.submitted_at ? 1 : 0;
      if (aSub !== bSub) return aSub - bSub;
      return (a.seq || 0) - (b.seq || 0);
    });
  }, [forms]);

  const filtered = useMemo(
    () => (nameFilter === "all" ? sorted : sorted.filter((f) => f.name === nameFilter)),
    [sorted, nameFilter]
  );

  useEffect(() => { setPage(1); }, [nameFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / FORMS_PER_PAGE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * FORMS_PER_PAGE, current * FORMS_PER_PAGE);

  return (
    <div data-testid="forms-tab">
      {forms.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Label className="text-xs text-slate-500">Filter by form</Label>
            <Select value={nameFilter} onValueChange={setNameFilter}>
              <SelectTrigger data-testid="forms-name-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="forms-name-all">All forms</SelectItem>
                {names.map((n) => <SelectItem key={n} value={n} data-testid={`forms-name-${n}`}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {forms.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="my-forms-empty">No forms assigned to you.</Card>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="my-forms-no-match">No forms match this filter.</Card>
      ) : (
        <div className="space-y-3">
          {visible.map((f) => (
            <FormCard
              key={f.id}
              f={f}
              readOnly={readOnly}
              onSubmit={onSubmit}
              submitting={submittingId === f.id}
              uploadsEnabled={uploadsEnabled}
            />
          ))}
        </div>
      )}
      <Pager page={current} pages={pages} onChange={setPage} testId="forms-pager" />
    </div>
  );
}

function HandoutsTab({ handouts }) {
  const [typeFilter, setTypeFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Most recent published first.
  const sorted = useMemo(
    () => [...handouts].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [handouts]
  );

  const types = useMemo(() => {
    const m = new Map();
    handouts.forEach((h) => { if (h.handout_type) m.set(h.handout_type, h.type_label || h.handout_type); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [handouts]);

  const names = useMemo(
    () => [...new Set(handouts.map((h) => h.name))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [handouts]
  );

  const filtered = useMemo(
    () => sorted.filter((h) => (typeFilter === "all" || h.handout_type === typeFilter) && (nameFilter === "all" || h.name === nameFilter)),
    [sorted, typeFilter, nameFilter]
  );

  useEffect(() => { setPage(1); }, [typeFilter, nameFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / HANDOUTS_PER_PAGE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * HANDOUTS_PER_PAGE, current * HANDOUTS_PER_PAGE);

  return (
    <div data-testid="handouts-tab">
      {handouts.length > 0 && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="w-full max-w-[220px]">
            <Label className="text-xs text-slate-500">Filter by type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="handouts-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="handouts-type-all">All types</SelectItem>
                {types.map(([val, label]) => <SelectItem key={val} value={val} data-testid={`handouts-type-${val}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full max-w-[220px]">
            <Label className="text-xs text-slate-500">Filter by handout</Label>
            <Select value={nameFilter} onValueChange={setNameFilter}>
              <SelectTrigger data-testid="handouts-name-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="handouts-name-all">All handouts</SelectItem>
                {names.map((n) => <SelectItem key={n} value={n} data-testid={`handouts-name-${n}`}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {handouts.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="my-handouts-empty">No handouts shared with you.</Card>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="my-handouts-no-match">No handouts match these filters.</Card>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => (
            <Card key={r.id} className="flex items-center justify-between gap-3 p-4" data-testid={`my-handout-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2"><Link2 className="h-4 w-4 shrink-0 text-orange-500" /><span className="truncate font-semibold text-slate-800">{r.name}</span></div>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                  {r.type_label && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{r.type_label}</span>}
                  {r.created_at && <span>Published {formatDateOnly(r.created_at)}</span>}
                </p>
              </div>
              {r.link && (
                <div className="flex shrink-0 gap-1">
                  <a href={r.link} target="_blank" rel="noopener noreferrer" data-testid={`open-handout-${r.id}`}><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open</Button></a>
                  <a href={r.link} target="_blank" rel="noopener noreferrer" download data-testid={`download-handout-${r.id}`}><Button variant="ghost" size="sm" className="rounded-full"><Download className="h-3.5 w-3.5" /></Button></a>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <Pager page={current} pages={pages} onChange={setPage} testId="handouts-pager" />
    </div>
  );
}

function FormCard({ f, readOnly, onSubmit, submitting, uploadsEnabled }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const canUpload = uploadsEnabled && f.has_folder;
  const submitted = f.status !== "not_submitted";
  const canSubmit = !readOnly && f.status !== "approved";

  const openDoc = () => f.link && window.open(f.link, "_blank", "noopener");

  const doSubmit = () => {
    onSubmit(f.id, { file }, () => { setOpen(false); setFile(null); });
  };

  return (
    <Card className="p-4" data-testid={`my-form-${f.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={openDoc}
          className={`min-w-0 text-left ${f.link ? "cursor-pointer" : "cursor-default"}`}
          data-testid={`my-form-open-${f.id}`}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{f.seq}</span>
            <FileText className="h-4 w-4 text-orange-500" />
            <span className="font-semibold text-slate-800">{f.name}</span>
            {f.link && <ExternalLink className="h-3.5 w-3.5 text-sky-500" />}
          </div>
          {f.description && <p className="mt-1 text-sm text-slate-500">{f.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <DocStatusBadge status={f.status} testId={`my-form-status-${f.id}`} />
            {f.due_date && <span>Due {formatDateOnly(f.due_date)}</span>}
            {f.submitted_at && <span data-testid={`my-form-submitted-${f.id}`}>Submitted {formatDateTime(f.submitted_at, { dateStyle: "medium", timeStyle: "short" })}</span>}
            {f.approved_at && <span className="font-medium text-emerald-600" data-testid={`my-form-approved-${f.id}`}>Approved {formatDateTime(f.approved_at, { dateStyle: "medium", timeStyle: "short" })}</span>}
          </div>
          {f.note && (f.status === "returned" || f.status === "rejected") && <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">Coach note: {f.note}</p>}
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {f.link && (
            <>
              <a href={f.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`open-form-${f.id}`}>
                <Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open</Button>
              </a>
              <a href={f.link} target="_blank" rel="noopener noreferrer" download onClick={(e) => e.stopPropagation()} data-testid={`download-form-${f.id}`}>
                <Button variant="ghost" size="sm" className="rounded-full"><Download className="h-3.5 w-3.5" /></Button>
              </a>
            </>
          )}
          {canSubmit && !open && (
            <Button size="sm" className="rounded-full" onClick={() => setOpen(true)} data-testid={`my-form-submit-btn-${f.id}`}>
              <Send className="mr-2 h-4 w-4" /> {submitted ? "Update" : "Submit"}
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`my-form-submit-panel-${f.id}`}>
          {canUpload && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`upload-disclaimer-${f.id}`}>
              <p className="text-xs leading-relaxed text-amber-800">
                By uploading this form, you acknowledge that your file will be sent to a Google Drive folder designated by your coach. This folder is an external account and is not owned or accessed by RallyPointGo. Uploading is optional and not required to submit your form. RallyPointGo only provides a private link to the folder and does not store, view, or manage any files uploaded through it. Your coach is solely responsible for the security and management of the uploaded files.
              </p>
            </div>
          )}
          <p className="text-sm text-slate-600" data-testid={`submit-help-${f.id}`}>Submitting records today's date and time automatically and sends it to your coach for review.</p>
          {canUpload && (
            <div className="mt-3">
              <input ref={fileRef} type="file" className="hidden" data-testid={`file-input-${f.id}`} onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid={`attach-btn-${f.id}`}>
                <Upload className="mr-2 h-4 w-4" /> {file ? "Change file" : "Attach file (optional)"}
              </Button>
            </div>
          )}
          {file && (
            <p className="mt-2 flex items-center gap-2 text-xs text-slate-600" data-testid={`file-chosen-${f.id}`}>
              <FileText className="h-3.5 w-3.5 text-orange-500" /> {file.name}
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-red-500" data-testid={`file-clear-${f.id}`}><X className="h-3.5 w-3.5" /></button>
            </p>
          )}
          {canUpload && <p className="mt-2 text-xs text-slate-400">Attaching a file is optional (max 25 MB).</p>}
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setOpen(false); setFile(null); }} data-testid={`submit-cancel-${f.id}`}>Cancel</Button>
            <Button size="sm" className="rounded-full" onClick={doSubmit} disabled={submitting} data-testid={`submit-confirm-${f.id}`}>
              {submitting ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
